'use client';

import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import type { ChangeEvent } from 'react';
import { useCallback, useState } from 'react';
import type { AddressAutocompleteProps, AddressFieldKey, Suggestion } from './types';
import { DEFAULT_ADDRESS_FIELDS } from './types';

/**
 * Pure MUI address autocomplete. Data-in / events-out: no react-hook-form,
 * no network calls, no Google Places coupling. A view component is
 * responsible for data fetching and wiring `value`/`onChange` to a form.
 *
 * Visual contract mirrors the legacy `Form/Address` component: a bold
 * section header with a toggle that switches between the single search
 * input and the 2-column manual entry grid.
 *
 * ADR 71 data attributes:
 *   - `data-testid="address-autocomplete"` on the root
 *   - `data-field=<key>` on every form input
 *   - `data-action="select-suggestion"` on each suggestion row
 *   - `data-action="toggle-mode"` on the mode toggle button
 */
export function AddressAutocomplete(props: AddressAutocompleteProps) {
  const {
    value,
    onChange,
    suggestions,
    isLoading,
    hasQueried,
    errorHint,
    onQueryChange,
    onSuggestionSelect,
    query,
    fields = DEFAULT_ADDRESS_FIELDS,
    title = 'Address',
    placeholder = 'Please enter your address',
    startInAutoComplete = true,
  } = props;

  const [isAutoCompleteMode, setIsAutoCompleteMode] = useState(startInAutoComplete);

  const handleFieldChange = useCallback(
    (field: AddressFieldKey) => (event: ChangeEvent<HTMLInputElement>) => {
      onChange({ ...value, [field]: event.target.value });
    },
    [onChange, value],
  );

  const showNoMatches = suggestions.length === 0 && hasQueried === true && !isLoading;

  return (
    <Box
      data-testid="address-autocomplete"
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        gap: theme.semantic.spacing.xs,
      })}
    >
      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <Typography variant="subtitle1" fontWeight="bold">
          {title}
        </Typography>
        <Button
          variant="text"
          onClick={() => setIsAutoCompleteMode((m) => !m)}
          data-action="toggle-mode"
        >
          {isAutoCompleteMode ? 'ENTER MANUALLY' : 'AUTO COMPLETE'}
        </Button>
      </Box>

      <Box sx={{ display: isAutoCompleteMode ? 'block' : 'none' }}>
        <Autocomplete<Suggestion, false, false, true>
          fullWidth
          freeSolo
          // Upstream drives the options; don't let MUI client-side filter.
          filterOptions={(options) => options}
          options={suggestions}
          getOptionLabel={(option) =>
            typeof option === 'string' ? option : option.label
          }
          isOptionEqualToValue={(option, selected) => option.placeId === selected.placeId}
          loading={isLoading}
          inputValue={query ?? ''}
          onInputChange={(_event, newInput) => {
            // Propagate every reason ('input' | 'reset' | 'clear') so the
            // controlled `query` stays in sync: 'input' on keystrokes,
            // 'reset' when MUI fills the input with the selected option's
            // label after a pick, and 'clear' when the user clicks the
            // clear (x) indicator. Without 'clear' the controlled input
            // would immediately re-render the old value.
            onQueryChange(newInput);
          }}
          onChange={(_event, selected) => {
            if (selected && typeof selected !== 'string') {
              onSuggestionSelect(selected.placeId);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              placeholder={placeholder}
              inputProps={{
                ...params.inputProps,
                'data-field': 'addressQuery',
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading ? (
                      <CircularProgress
                        role="progressbar"
                        size={20}
                      />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(liProps, option) => (
            <Box
              component="li"
              {...liProps}
              key={option.placeId}
              role="option"
              data-action="select-suggestion"
              sx={(theme) => ({
                gap: theme.semantic.spacing.xs,
                alignItems: 'center',
              })}
            >
              <LocationOnOutlinedIcon
                fontSize="small"
                sx={{ color: 'text.secondary', flexShrink: 0 }}
              />
              <Typography variant="body2">{option.label}</Typography>
            </Box>
          )}
        />
        {errorHint ? (
          <Typography variant="caption" color="error" role="alert">
            {errorHint}
          </Typography>
        ) : null}
        {showNoMatches ? (
          <Typography
            variant="body2"
            sx={(theme) => ({
              color: 'text.secondary',
              marginTop: theme.semantic.spacing.xs,
            })}
          >
            No matches. Please enter your address manually.
          </Typography>
        ) : null}
      </Box>

      <Box
        sx={(theme) => ({
          display: isAutoCompleteMode ? 'none' : 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: theme.semantic.spacing.sm,
        })}
      >
        {fields.map(({ key, label, required }) => (
          <TextField
            key={key}
            fullWidth
            variant="outlined"
            label={label}
            required={required}
            value={(value as Record<AddressFieldKey, string | undefined>)[key] ?? ''}
            onChange={handleFieldChange(key)}
            inputProps={{ 'data-field': key }}
          />
        ))}
      </Box>
    </Box>
  );
}

export default AddressAutocomplete;
